import { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import {
  useGetSocietyByCodeQuery,
  useGetSocietySettingsQuery,
} from '@/publicSearch/graphql/queries.generated';
import { Society } from '@/publicSearch/store/usePublicSearchContext';

export type CurrentSociety = Society & {
  /**
   * True when the URL supplied a societyCode, the society lookup has
   * finished (or errored), and no matching society was returned. Consumers
   * should render a "society not found" experience in this case.
   */
  notFound: boolean;
};

export function useCurrentSociety(): CurrentSociety {
  const pathParams = useParams<{ orgName?: string }>();
  const societyCode = pathParams.orgName?.trim() || '';

  // Look up organisation by societyCode (orgName in URL is now societyCode).
  // The societyCode -> society mapping is keyed by a real query variable, so
  // cache-first is safe here and avoids refetching on same-society navigation.
  const {
    data: societyData,
    loading: societyLoading,
    error: societyError,
  } = useGetSocietyByCodeQuery({
    skip: !societyCode,
    variables: { societyCode },
    fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
    nextFetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
  });

  const society = societyData?.societies?.items?.[0];
  const orgId = String(society?.societyId || '');

  const { data: settingsData, loading: settingsLoading } =
    useGetSocietySettingsQuery({
      skip: !orgId,
      context: { headers: setDefaultHeader(orgId) },
      // This query is scoped by X-Society-Id headers, not variables. Apollo's
      // cache key does not include headers, so cache-first can leak the
      // previous society's settings when navigating between societies.
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      nextFetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    });

  const loading = societyLoading || settingsLoading;
  const settings = settingsData?.societySettings;

  // Apollo v4's useQuery doesn't expose `called` on the result, but when
  // `skip: true` (no societyCode) loading is false immediately — the
  // `!!societyCode` guard handles that case. A network-level failure
  // shouldn't be treated as "not found"; let the caller surface retry/error
  // UI. An empty items[] on a successful query is the signal we want.
  const notFound =
    !!societyCode && !societyLoading && !societyError && !society;

  return useMemo(
    () =>
      ({
        ...society,
        settings,
        loading,
        name: society?.name || '',
        orgId,
        societyCode: society?.societyCode || '',
        notFound,
      }) as CurrentSociety,
    [loading, orgId, society, settings, notFound],
  );
}

/* old impl removed:
  const { data, loading } = useQuery<GetSocietySettingsResponse>(
    GET_SOCIETY_SETTINGS,
    {
      skip: !societyCode,
      variables: {
        societyCode: societyCode,
      },
    },
  );

  const society = data?.societies?.items?.[0];
  const settings = useMemo(() => {
    const allSettings = data?.societySettings;
    if (!allSettings || allSettings.length === 0) return undefined;
    if (!society?.societyId) return allSettings;

    // If the API returns settings across societies, filter down.
    // If it already returns only the current society's settings, the filter is a no-op.
    return allSettings.filter(
      (s) => s.societyId == null || s.societyId === society.societyId,
    );
  }, [data?.societySettings, society?.societyId]);

  const orgId = String(
    society?.breedLogicSocietyId || society?.societyId || '',
  );

  return useMemo(
    () =>
      ({
        ...society,
        settings,
        loading,
        name: society?.name || '',
        orgId,
        societyCode: society?.societyCode || '',
      }) as Society,
    [loading, orgId, society, settings],
  );
}
*/
